import React, { useState, useEffect } from 'react';
import Navbar from "../../../Shared/Navbar/Navbar";
import { Table, Select, Button, DatePicker, InputNumber, Input, Modal, Form, Card, Tag, Space, Spin, message } from 'antd';
import { CloseOutlined, FilterOutlined } from '@ant-design/icons';
import axios from 'axios';
import 'antd/dist/reset.css';
import { baseURL } from "../../../Apiservices/Api";
import { useParams, useNavigate } from "react-router-dom";
   
import "./Report.css";
const { Option } = Select;
const { RangePicker } = DatePicker;

// Field definitions based on API response
const allFields = [
  { key: 'leadid', label: 'Lead ID', type: 'number' },
  { key: 'name', label: 'Name', type: 'text' },
  { key: 'email', label: 'Email', type: 'text' },
  { key: 'phone_number', label: 'Phone', type: 'text' },
  { key: 'status', label: 'Status', type: 'text' },
  { key: 'primaryStatus', label: 'Primary Status', type: 'text' },
  { key: 'secondaryStatus', label: 'Secondary Status', type: 'text' },
  { key: 'primarySource', label: 'Primary Source', type: 'text' },
  { key: 'secondarysource', label: 'Secondary Source', type: 'text' },
  { key: 'destination', label: 'Destination', type: 'text' },
  { key: 'assign_to_manager', label: 'Manager', type: 'text' },
  { key: 'assignedSalesName', label: 'Associate', type: 'text' },
  { key: 'description', label: 'Description', type: 'text' },
  { key: 'created_at', label: 'Created At', type: 'date' },
];

const ReportBuilder = () => {
const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState("reportcustomer");
  const [collapsed, setCollapsed] = useState(false);
  const [rawData, setRawData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [selectedFields, setSelectedFields] = useState(['name', 'email', 'phone_number', 'status']);
  const [activeFilters, setActiveFilters] = useState([]);
  const [availableFilterFields, setAvailableFilterFields] = useState([]);
  const [selectedFilterField, setSelectedFilterField] = useState(null);
  const [savedReports, setSavedReports] = useState([]);
  const [isSaveModalVisible, setIsSaveModalVisible] = useState(false);
  const [reportName, setReportName] = useState('');
  const [reportDescription, setReportDescription] = useState('');
  const [selectedReport, setSelectedReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showSavedReports, setShowSavedReports] = useState(false);

  // Load saved reports from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('savedLeadReports');
    if (saved) {
      setSavedReports(JSON.parse(saved));
    }
  }, []);

  useEffect(() => {
    const fetchSavedReports = async () => {
      try {
        const response = await axios.get(`${baseURL}/api/reports`);
        setSavedReports(response.data);
      } catch (error) {
        message.error('Failed to fetch saved reports');
        console.error('Error fetching reports:', error);
      }
    };

    fetchSavedReports();
  }, []);

  // Save reports to localStorage
  useEffect(() => {
    localStorage.setItem('savedLeadReports', JSON.stringify(savedReports));
  }, [savedReports]);

  // Fetch data from API
  useEffect(() => {
    const fetchLeads = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${baseURL}/api/allleads`);
        setRawData(response.data);
        setFilteredData(response.data);
      } catch (error) {
        message.error('Failed to fetch leads');
        console.error('Error fetching leads:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchLeads();
  }, []);

  // Update available filter fields
  useEffect(() => {
    const filtered = applyFilters(rawData);
    setFilteredData(filtered);
  }, [selectedFields, activeFilters, rawData]);

  // Generate columns for the table
  const generateColumns = () => {
    return selectedFields.map(fieldKey => {
      const field = allFields.find(f => f.key === fieldKey);
      return {
        title: field.label,
        dataIndex: fieldKey,
        key: fieldKey,
        sorter: (a, b) => {
          if (field.type === 'number') return (a[fieldKey] || 0) - (b[fieldKey] || 0);
          return String(a[fieldKey] || '').localeCompare(String(b[fieldKey] || ''));
        },
        render: value => value || '-',
      };
    });
  };

  const applyFilters = (data) => {
    if (!data || data.length === 0) return [];
    
    let filteredData = [...data];
    
    activeFilters.forEach(filter => {
      const { field, value } = filter;
      
      if (field.type === 'text') {
        if (value && value.length > 0) {
          filteredData = filteredData.filter(item => {
            const itemValue = item[field.key] || '';
            return value.some(val => 
              String(itemValue).toLowerCase().includes(String(val).toLowerCase())
            );
          });
        }
      } 
      else if (field.type === 'number') {
        if (value?.min !== undefined && value?.min !== null) {
          filteredData = filteredData.filter(item => (item[field.key] || 0) >= value.min);
        }
        if (value?.max !== undefined && value?.max !== null) {
          filteredData = filteredData.filter(item => (item[field.key] || 0) <= value.max);
        }
      }
      else if (field.type === 'date' && value && value.length === 2) {
        const [start, end] = value;
        filteredData = filteredData.filter(item => {
          const itemDate = item[field.key] ? new Date(item[field.key]) : null;
          return itemDate && itemDate >= start && itemDate <= end;
        });
      }
    });
    
    return filteredData;
  };

  // Filter components
  const filterComponents = {
    text: ({ field, value, onChange, onRemove }) => {
      const uniqueValues = [...new Set(rawData.map(item => item[field.key]))];
      return (
        <Select
          mode="multiple"
          style={{ width: 200 }}
          placeholder={`Select ${field.label}`}
          value={value}
          onChange={onChange}
          dropdownRender={menu => (
            <div>
              {menu}
              <div style={{ padding: '5px', textAlign: 'right' }}>
                <Button size="small" danger onClick={onRemove}>
                  Remove Filter
                </Button>
              </div>
            </div>
          )}
        >
          {uniqueValues.map(val => (
            <Option key={val} value={val}>{val || '(Empty)'}</Option>
          ))}
        </Select>
      );
    },
    number: ({ field, value, onChange, onRemove }) => (
      <Space>
        <InputNumber
          placeholder={`Min ${field.label}`}
          value={value?.min}
          onChange={min => onChange({ ...value, min })}
          style={{ width: 100 }}
        />
        <InputNumber
          placeholder={`Max ${field.label}`}
          value={value?.max}
          onChange={max => onChange({ ...value, max })}
          style={{ width: 100 }}
        />
        <Button danger icon={<CloseOutlined />} onClick={onRemove} />
      </Space>
    ),
    date: ({ field, value, onChange, onRemove }) => (
      <Space>
        <RangePicker
          value={value}
          onChange={onChange}
        />
        <Button danger icon={<CloseOutlined />} onClick={onRemove} />
      </Space>
    )
  };

  // Generate filtered data when fields or filters change
  useEffect(() => {
    const available = allFields
      .filter(field => !activeFilters.some(f => f.field.key === field.key));
    setAvailableFilterFields(available);
    setSelectedFilterField(available[0]?.key || null);
  }, [activeFilters]);

  // Handler functions
  const handleFieldChange = (values) => setSelectedFields(values);

  const addFilter = () => {
    if (!selectedFilterField) return;
    const field = allFields.find(f => f.key === selectedFilterField);
    if (!field) return;
    const newFilter = {
      id: Date.now(),
      field,
      value: field.type === 'number' ? { min: null, max: null } : 
             field.type === 'date' ? null : []
    };
    setActiveFilters([...activeFilters, newFilter]);
  };

  const updateFilter = (id, newValue) => {
    setActiveFilters(activeFilters.map(filter => 
      filter.id === id ? { ...filter, value: newValue } : filter
    ));
  };

  const removeFilter = (id) => {
    setActiveFilters(activeFilters.filter(filter => filter.id !== id));
  };

  const exportToCSV = () => {
    const headers = selectedFields.map(field => {
      const fieldInfo = allFields.find(f => f.key === field);
      return fieldInfo.label;
    }).join(',');
    
    const rows = filteredData.map(item => {
      return selectedFields.map(field => item[field] || '').join(',');
    }).join('\n');
    
    const csvContent = `${headers}\n${rows}`;
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'leads_report.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const showSaveModal = () => setIsSaveModalVisible(true);

  const toggleSavedReports = () => setShowSavedReports(!showSavedReports);

  const saveReportToBoth = async (report) => {
    try {
      const response = await axios.post(`${baseURL}/api/reports`, report);
      const newReport = response.data;
      
      setSavedReports(prev => {
        const updatedReports = [...prev, newReport];
        localStorage.setItem('savedLeadReports', JSON.stringify(updatedReports));
        return updatedReports;
      });
      
      return newReport;
    } catch (error) {
      console.error('Error saving report to API:', error);
      const newReport = {
        ...report,
        id: Date.now(),
        createdAt: new Date().toISOString()
      };
      
      setSavedReports(prev => {
        const updatedReports = [...prev, newReport];
        localStorage.setItem('savedLeadReports', JSON.stringify(updatedReports));
        return updatedReports;
      });
      
      return newReport;
    }
  };

  const deleteReportFromBoth = async (reportId) => {
    try {
      await axios.delete(`${baseURL}/api/reports/${reportId}`);
    } catch (error) {
      console.error('Error deleting report from API:', error);
    }
    
    setSavedReports(prev => {
      const updatedReports = prev.filter(report => report.id !== reportId);
      localStorage.setItem('savedLeadReports', JSON.stringify(updatedReports));
      return updatedReports;
    });
  };

  const handleSaveReport = async () => {
    if (!reportName.trim()) {
      message.error('Please enter a report name');
      return;
    }

    try {
      const newReport = {
        name: reportName,
        description: reportDescription,
        selectedFields: [...selectedFields],
        activeFilters: [...activeFilters],
      };

      await saveReportToBoth(newReport);
      
      setIsSaveModalVisible(false);
      setReportName('');
      setReportDescription('');
      message.success('Report saved successfully');
      setShowSavedReports(true); // Show saved reports after saving
    } catch (error) {
      message.error('Failed to save report');
      console.error('Error saving report:', error);
    }
  };

  const deleteReport = async (reportId, e) => {
    e.stopPropagation();
    try {
      await deleteReportFromBoth(reportId);
      if (selectedReport === reportId) {
        setSelectedReport(null);
      }
      message.success('Report deleted');
    } catch (error) {
      message.error('Failed to delete report');
      console.error('Error deleting report:', error);
    }
  };

  const loadReport = (report) => {
    setSelectedFields(report.selectedFields);
    setActiveFilters(report.activeFilters);
    setSelectedReport(report.id);
    message.info(`Loaded report: ${report.name}`);
    setShowSavedReports(false); // Hide saved reports after loading one
  };

  return (
    <div className="Admin-reportcontainer">
      <Navbar onToggleSidebar={setCollapsed} />
      <div className={`Admin-report ${collapsed ? "collapsed" : ""}`}>
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>

        <div className="payments-tabs">
                    <button
                        className={activeTab === "reportleads" ? "active" : ""}
                        onClick={() => {
                            setActiveTab("reportleads");
                            navigate(`/A_Report/`);
                           
                        }}
                    >
                        Leads
                    </button>
                    <button
                        className={activeTab === "reportopp" ? "active" : ""}
                        onClick={() =>{ setActiveTab("reportopp") 
                          navigate(`/A_Report_opp/`);
                        }}
                        
                    >
                        Opportunity
                    </button>
                    <button
                        className={activeTab === "reportcustomer" ? "active" : ""}
                        onClick={() =>{ setActiveTab("reportcustomer")
navigate(`/A_Report_customer/`);
                        }}
                    >
                        Customer
                    </button>
                </div>
          <div><h3 className='text-center'>Reports</h3></div>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '16px',
            gap: '16px',
            flexWrap: 'wrap'
          }}>
            <div style={{ flex: 1, minWidth: '300px' }}>
              <Select
                mode="multiple"
                style={{ width: '100%' }}
                placeholder="Select fields to include in report"
                value={selectedFields}
                onChange={handleFieldChange}
              >
                {allFields.map(field => (
                  <Option key={field.key} value={field.key}>{field.label}</Option>
                ))}
              </Select>
            </div>
            
            <Space>
              <Button onClick={exportToCSV} type="primary">
                Export to CSV
              </Button>
              <Button onClick={showSaveModal}>
                Save Report
              </Button>
              <Button onClick={toggleSavedReports}>
                {showSavedReports ? 'Hide Saved' : 'Show Saved'}
              </Button>
            </Space>
          </div>

          {/* Filters row with horizontal scroll */}
          <div style={{ 
            marginBottom: '16px',
            padding: '12px',
            backgroundColor: '#fafafa',
            border: '1px solid #d9d9d9',
            borderRadius: '4px',
            overflowX: 'auto',
            whiteSpace: 'nowrap'
          }}>
            <Space size="middle">
              {activeFilters.length > 0 ? (
                activeFilters.map(filter => {
                  const FilterComponent = filterComponents[filter.field.type];
                  return (
                    <div key={filter.id} style={{ 
                      display: 'inline-block',
                      padding: '8px',
                      border: '1px solid #d9d9d9',
                      borderRadius: '4px',
                      backgroundColor: 'white'
                    }}>
                      <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                        {filter.field.label}
                      </div>
                      <FilterComponent
                        field={filter.field}
                        value={filter.value}
                        onChange={(value) => updateFilter(filter.id, value)}
                        onRemove={() => removeFilter(filter.id)}
                      />
                    </div>
                  );
                })
              ) : (
                <span style={{ color: '#999' }}>No filters applied</span>
              )}
            </Space>
            
            <div style={{ display: 'inline-block', marginLeft: '16px', verticalAlign: 'top' }}>
              <Space>
                <Select
                  style={{ width: 200 }}
                  value={selectedFilterField}
                  onChange={setSelectedFilterField}
                  disabled={availableFilterFields.length === 0}
                >
                  {availableFilterFields.map(field => (
                    <Option key={field.key} value={field.key}>{field.label}</Option>
                  ))}
                </Select>
                
                <Button 
                  type="primary" 
                  onClick={addFilter}
                  disabled={availableFilterFields.length === 0}
                >
                  Add Filter
                </Button>
                
                {activeFilters.length > 0 && (
                  <Button 
                    danger 
                    onClick={() => setActiveFilters([])}
                  >
                    Clear All
                  </Button>
                )}
              </Space>
            </div>
          </div>

          {/* Main content area */}
          <div style={{ overflowX: 'auto' }}>
  <div style={{ 
    display: 'flex', 
    flex: 1, 
    gap: '16px',
    minWidth: '1000px' // or whatever is larger than combined widths
  }}>
    {/* Lead Report Table */}
    <div style={{ 
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      minWidth: 0 
    }}>
      <div style={{ marginBottom: '8px' }}>
        <h3>Lead Report ({filteredData.length} records)</h3>
      </div>
      <div style={{ flex: 1 }}>
        <Spin spinning={loading}>
          <Table 
            columns={generateColumns()} 
            dataSource={filteredData} 
            rowKey="leadid"
            pagination={{ pageSize: 10 }}
            bordered
            scroll={{ x: 'max-content', y: '100%' }}
            style={{ minWidth: '100%' }}
          />
        </Spin>
      </div>
    </div>

    {/* Saved Reports Sidebar */}
    {showSavedReports && (
      <div style={{ 
        width: '300px',
        borderLeft: '1px solid #d9d9d9',
        paddingLeft: '16px',
        overflowY: 'auto'
      }}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '16px'
        }}>
          <h3>Saved Reports</h3>
          <Button 
            size="small" 
            icon={<CloseOutlined />} 
            onClick={() => setShowSavedReports(false)}
          />
        </div>
        
        {savedReports.length === 0 ? (
          <p>No saved reports yet</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {savedReports.map(report => (
              <Card 
                key={report.id}
                onClick={() => loadReport(report)}
                size="small"
                style={{ 
                  cursor: 'pointer',
                  borderColor: selectedReport === report.id ? '#1890ff' : '#d9d9d9',
                  backgroundColor: selectedReport === report.id ? '#e6f7ff' : 'white'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <div style={{ flex: 1, overflow: 'hidden' }}>
                    <h4 style={{ 
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      margin: 0
                    }}>
                      {report.name}
                    </h4>
                    <p style={{ color: '#666', fontSize: '12px', margin: '4px 0' }}>
                      {new Date(report.created_at).toLocaleString()}
                    </p>
                    {report.description && (
                      <p style={{ 
                        fontSize: '12px',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        margin: 0
                      }}>
                        {report.description}
                      </p>
                    )}
                    <div style={{ marginTop: '4px' }}>
                      <Tag color="blue" style={{ marginRight: 0 }}>{report.selectedFields.length} cols</Tag>
                      <Tag color="orange">{report.activeFilters.length} filters</Tag>
                    </div>
                  </div>
                  <Button 
                    danger 
                    size="small" 
                    onClick={(e) => deleteReport(report.id, e)}
                    style={{ marginLeft: '8px' }}
                  >
                    Delete
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    )}
  </div>
</div>

        </div>

        {/* Save Report Modal */}
        <Modal
          title="Save Report Configuration"
          open={isSaveModalVisible}
          onOk={handleSaveReport}
          onCancel={() => setIsSaveModalVisible(false)}
          width={700}
        >
          <Form layout="vertical">
            <Form.Item label="Report Name" required>
              <Input 
                value={reportName}
                onChange={(e) => setReportName(e.target.value)}
                placeholder="Enter report name"
              />
            </Form.Item>
            <Form.Item label="Description">
              <Input.TextArea
                value={reportDescription}
                onChange={(e) => setReportDescription(e.target.value)}
                placeholder="Enter description (optional)"
                rows={3}
              />
            </Form.Item>
            <Form.Item label="Selected Fields">
              <div style={{ 
                padding: '10px', 
                background: '#f5f5f5', 
                borderRadius: '4px',
                maxHeight: '150px',
                overflowY: 'auto'
              }}>
                {selectedFields.map(field => {
                  const fieldInfo = allFields.find(f => f.key === field);
                  return <div key={field}>{fieldInfo.label}</div>;
                })}
              </div>
            </Form.Item>
            <Form.Item label="Active Filters">
              <div style={{ 
                padding: '10px', 
                background: '#f5f5f5', 
                borderRadius: '4px',
                maxHeight: '150px',
                overflowY: 'auto'
              }}>
                {activeFilters.length === 0 ? (
                  <div>No active filters</div>
                ) : (
                  activeFilters.map(filter => (
                    <div key={filter.id} style={{ marginBottom: '5px' }}>
                      <strong>{filter.field.label}: </strong>
                      {filter.field.type === 'text' && filter.value.join(', ')}
                      {filter.field.type === 'number' && [
                        filter.value.min && `Min: ${filter.value.min}`,
                        filter.value.max && `Max: ${filter.value.max}`
                      ].filter(Boolean).join(' | ')}
                      {filter.field.type === 'date' && filter.value && 
                        `${filter.value[0].format('YYYY-MM-DD')} to ${filter.value[1].format('YYYY-MM-DD')}`
                      }
                    </div>
                  ))
                )}
              </div>
            </Form.Item>
          </Form>
        </Modal>
      </div>
    </div>
  );
};

export default ReportBuilder;